import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleTags?: string[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  articlePublishedTime,
  articleModifiedTime,
  articleTags,
  jsonLd,
}: SEOHeadProps) {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') {
      return null;
    }

    document.title = title;
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    setLinkTag('canonical', canonicalUrl);

    setMetaTag('og:title', ogTitle || title);
    setMetaTag('og:description', ogDescription || description);
    if (ogImage) {
      setMetaTag('og:image', ogImage);
    }
    setMetaTag('og:url', canonicalUrl);

    setMetaTag('twitter:title', ogTitle || title);
    setMetaTag('twitter:description', ogDescription || description);

    if (articlePublishedTime) {
      setMetaTag('article:published_time', articlePublishedTime);
    }
    if (articleModifiedTime) {
      setMetaTag('article:modified_time', articleModifiedTime);
    }
    if (articleTags && articleTags.length > 0) {
      removeMetaTags('article:tag');
      articleTags.forEach((tag) => {
        addMetaTag('article:tag', tag);
      });
    }

    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((s) => {
      if (s.id && s.id !== 'site-org' && s.id !== 'site-web') {
        s.remove();
      }
    });

    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = `ld-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
      });
    }

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach((s) => {
        if (s.id && s.id !== 'site-org' && s.id !== 'site-web') {
          s.remove();
        }
      });
      removeMetaTags('article:tag');
      removeMetaTags('article:published_time');
      removeMetaTags('article:modified_time');
    };
  }, [location.pathname]);

  return null;
}

function setMetaTag(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    if (name.startsWith('og:') || name.startsWith('article:')) {
      meta.setAttribute('property', name);
    } else {
      meta.setAttribute('name', name);
    }
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function addMetaTag(name: string, content: string) {
  const meta = document.createElement('meta');
  meta.setAttribute('property', name);
  meta.setAttribute('content', content);
  document.head.appendChild(meta);
}

function removeMetaTags(name: string) {
  const tags = document.querySelectorAll(`meta[property="${name}"]`);
  tags.forEach((t) => t.remove());
}

function setLinkTag(rel: string, href: string) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}